// @generated by `pnpm content:generate` from src/content-export — DO NOT EDIT
import type { TravelerPack } from '../types.js'

export const IT: TravelerPack = {
  jurisdiction: {
    id: 'IT',
    type: 'country',
    name: 'Italy',
  },
  metros: [
    {
      id: 'IT-FLR',
      type: 'metro',
      name: 'Florence',
      parent: 'IT',
    },
    {
      id: 'IT-ROM',
      type: 'metro',
      name: 'Rome',
      parent: 'IT',
    },
  ],
  quickFacts: {
    driveSide: 'right',
    motorwayLimitKmh: 130,
    alcoholLimit: '0.05% (0.0 if licence < 3 yrs)',
    vignette: 'None (autostrada tolls)',
    emergency: '112',
  },
  rules: [
    {
      id: 'it-ztl-florence',
      target: 'IT-FLR',
      category: 'zone',
      severity: 'high',
      title: 'ZTL Firenze (historic centre)',
      detail: 'Camera-enforced limited-traffic zone; tourists are fined per crossing, often repeatedly.',
      cost: '€80–335',
      citation: 'https://www.comune.fi.it/pagina/ztl-zona-traffico-limitato',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'it-vignette',
      target: 'IT',
      category: 'vignette_toll',
      severity: 'info',
      title: 'None — autostrada tolls instead of a vignette',
      detail: 'Italy charges autostrada tolls per trip instead of a vignette.',
      cost: 'clear',
      citation: 'https://www.autostrade.it/en/the-network',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'it-speed-tutor',
      target: 'IT',
      category: 'limit',
      severity: 'high',
      title: 'Average-speed (Tutor) cameras',
      detail: 'Motorways measure average speed over long stretches; braking for one camera does not help.',
      cost: 'income-scaled',
      citation: 'https://www.poliziadistato.it/articolo/tutor',
      lastReviewed: '2026-06-02',
    },
    {
      id: 'it-idp',
      target: 'IT',
      category: 'document',
      severity: 'high',
      title: 'International Driving Permit required',
      detail: 'Non-EU licences (e.g. Indian) require an IDP alongside the home licence + passport.',
      citation: 'https://www.aci.it/i-servizi/per-la-mobilita/patente-internazionale.html',
      lastReviewed: '2026-06-02',
    },
  ],
  checklists: {
    pre: [
      {
        id: 'it-pre-idp',
        label: 'Apply for your International Driving Permit',
        detail: 'Non-EU drivers must carry an IDP with the home licence; allow weeks for issuance.',
        citation: 'https://www.aci.it/i-servizi/per-la-mobilita/patente-internazionale.html',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'it-pre-insurance',
        label: 'Confirm rental insurance + CDW coverage',
        detail: 'Check what your card or policy covers and whether ZTL/camera fines are passed through with an admin fee.',
      },
      {
        id: 'it-pre-tolls',
        label: 'Set up a tolls + parking app',
        detail: 'Install an autostrada toll/parking-payment app so you are not caught out at unmanned booths.',
      },
      {
        id: 'it-pre-offline',
        label: 'Download the offline city + rules pack',
        detail: 'Save offline maps and your ZTL boundary so you can navigate without data.',
      },
    ],
    on: [
      {
        id: 'it-on-ztl',
        label: 'Know the ZTL boundary before you enter the centre',
        detail: 'Cameras fine per crossing; if your hotel is inside a ZTL, get its plate registered in advance.',
        citation: 'https://www.comune.fi.it/pagina/ztl-zona-traffico-limitato',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'it-on-park',
        label: 'Park outside the zone and walk in',
        detail: 'Blue lines = paid, white = free (often residents-only), yellow = reserved. When unsure, park outside.',
      },
      {
        id: 'it-on-docs',
        label: 'Keep licence, IDP and passport reachable',
        detail: 'Carry the original documents — police checks expect all three for non-EU drivers.',
      },
    ],
    post: [
      {
        id: 'it-post-portal',
        label: 'Check the official fine / ZTL portal',
        detail: 'Camera fines arrive weeks later via the rental company; check the comune portal proactively.',
        citation: 'https://www.comune.fi.it/pagina/ztl-zona-traffico-limitato',
        lastReviewed: '2026-06-02',
      },
      {
        id: 'it-post-discount',
        label: 'Pay inside the reduced-amount window',
        detail: 'Many Italian fines drop ~30% if paid within 5 days — do not let the rental admin delay you past it.',
      },
      {
        id: 'it-post-dispute',
        label: 'Know your dispute rights',
        detail: 'You can contest a fine; keep rental dates and photos in case you were not the driver on the cited date.',
      },
    ],
  },
  course: {
    title: 'Driving Abroad Confidently',
    modules: [
      {
        id: 'it-m1-before',
        title: 'Before You Go',
        minutes: 5,
        body: 'Italy welcomes foreign drivers, but a few documents make or break the trip. Non-EU licence holders must carry an International Driving Permit alongside their national licence and passport; the IDP is only a translation, so the original licence still has to be on you. Sort rental insurance early and read exactly how the company handles camera fines — most add an admin fee and forward your details to the authority. Finally, set up a toll and parking app and download offline maps. The single biggest tourist mistake is arriving unprepared for Italy’s zone and camera enforcement, which is automated and unforgiving.',
        keyPoints: [
          'Carry IDP + national licence + passport together',
          'Confirm how the rental handles forwarded fines',
          'Install toll/parking apps and offline maps before you fly',
        ],
      },
      {
        id: 'it-m2-car',
        title: 'The Car & The Side',
        minutes: 5,
        body: 'Italy drives on the right, and rental cars are usually manual unless you book automatic explicitly. Motorway (autostrada) limits run up to 130 km/h in good conditions, dropping in rain. Keep right except to overtake — Italian drivers expect the left lane to clear quickly. Headlights are required on motorways even in daylight. Get comfortable with roundabouts, which are common, and with narrow historic streets where mirrors fold for a reason. Spend ten minutes in the rental lot finding the lights, wipers, and hazard switch before you join fast traffic, so nothing surprises you at speed.',
        keyPoints: [
          'Drive on the right; book automatic if you need it',
          'Keep right except to overtake on the autostrada',
          'Daytime headlights required on motorways',
        ],
      },
      {
        id: 'it-m3-surprise',
        title: 'Rules That Surprise You',
        minutes: 5,
        body: 'The rules that catch tourists are rarely about speed alone. Florence, Rome, Milan and dozens of towns run ZTLs — limited-traffic zones in their historic centres, watched by cameras around the clock or during posted hours. Drive in without authorisation and you are fined per crossing, sometimes several times in one visit. Average-speed “Tutor” cameras on motorways measure your speed between two points, so slowing for a single camera does nothing. Alcohol limits are strict — zero tolerance for drivers licensed under three years. Treat the historic centre as off-limits by car unless your accommodation has registered your plate.',
        keyPoints: [
          'ZTLs fine you per camera crossing',
          'Tutor cameras measure average, not spot, speed',
          'Zero alcohol tolerance for newer licences',
        ],
      },
      {
        id: 'it-m4-fines',
        title: 'The Hidden Fine Traps',
        minutes: 5,
        body: 'The most expensive part of an Italian trip often arrives weeks after you fly home. Camera and ZTL fines are routed through your rental company, which forwards your address to the authority and bills an admin fee. The trap is timing: many fines offer a reduced amount if paid within about five days, but by the time the rental paperwork reaches you the discount window may be closing. Check the relevant comune’s fine portal proactively rather than waiting for mail. If you believe a fine is wrong — wrong date, not the driver — you can contest it, so keep your rental dates and any photos.',
        keyPoints: [
          'Fines arrive weeks later via the rental company',
          'Reduced-amount windows are short — act fast',
          'Keep evidence so you can dispute errors',
        ],
        check: {
          q: 'Italian camera fines often offer a reduced amount if you pay within about five days. True or false?',
          options: [
            {
              label: 'True — pay inside the discount window',
              correct: true,
            },
            {
              label: 'False — the amount is always fixed',
              correct: false,
            },
          ],
        },
      },
      {
        id: 'it-m5-wrong',
        title: 'When Things Go Wrong',
        minutes: 5,
        body: 'In any emergency dial 112, the single European number that reaches police, ambulance and fire. After a minor collision, move to safety, switch on hazards and place the warning triangle, and put on a reflective vest before stepping onto the roadway. Exchange details using the European Accident Statement (constat amiable) — a standard bilingual form most insurers provide — rather than relying on memory. Photograph positions, plates and damage. Do not admit liability on the spot; let the insurers and the form do their work. For anything involving injury, theft or a dispute, wait for the authorities and keep your documents to hand.',
        keyPoints: [
          'Dial 112 for all emergencies',
          'Hazards + triangle + hi-vis vest before you exit',
          'Use the European Accident Statement; photograph everything',
        ],
      },
      {
        id: 'it-m6-applied',
        title: 'Your Trip Applied',
        minutes: 5,
        body: 'Put it together for a typical Italy visit. Before leaving home: IDP in hand, insurance confirmed, toll and parking apps installed, offline pack downloaded. On the ground: park outside the historic centre and walk in, keep your documents reachable, and never cross a ZTL line on a guess. Day to day: keep right, mind the average-speed cameras, and stay well under the alcohol limit. After you return: check the comune fine portal, pay inside the discount window, and dispute anything that looks wrong. Follow this rhythm and Italy’s automated enforcement becomes a non-event instead of an expensive surprise.',
        keyPoints: [
          'Pre-trip: documents, insurance, apps, offline pack',
          'On-trip: park outside the ZTL, keep right, stay sober',
          'Post-trip: check the portal and pay or dispute promptly',
        ],
      },
    ],
  },
  emergency: {
    number: '112',
    steps: [
      {
        title: 'After an accident',
        detail: 'Stop, move to safety, switch on hazards and check everyone. Call 112 for injuries; give your location and number of people hurt.',
      },
      {
        title: 'European Accident Statement',
        detail: 'Complete the constat amiable / European Accident Statement with the other driver — it is standardised and bilingual, so use it instead of free-form notes.',
      },
      {
        title: 'Hazard signal + triangle',
        detail: 'Put on your reflective vest before leaving the car and place the warning triangle well back up the road to warn approaching traffic.',
      },
    ],
    phrases: [
      {
        en: 'I need an ambulance',
        local: 'Ho bisogno di un’ambulanza',
      },
      {
        en: 'I don\'t understand',
        local: 'Non capisco',
      },
      {
        en: 'Where is the nearest hospital?',
        local: 'Dov’è l’ospedale più vicino?',
      },
    ],
  },
}
